import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { driverApi, DriverDocument, DriverMe } from '../../services/driverApi';
import { resolveEgovLaunchHref } from '../../utils/egovLaunch';
import { DriverCopy } from './driverI18n';
import { docTitle, isSignable, statusColor } from './driverDoc';
import { DriverHeader } from './DriverHeader';

interface Props {
  t: DriverCopy;
  userName?: string;
  billing?: DriverMe['billing'];
  onMenu: () => void;
  onOpenDoc: (id: string) => void;
  onPay?: () => void;
}

type Tab = 'to_sign' | 'signed';

export const DriverDocumentsPage: React.FC<Props> = ({
  t,
  userName,
  billing,
  onMenu,
  onOpenDoc,
  onPay,
}) => {
  const [documents, setDocuments] = useState<DriverDocument[]>([]);
  const [listBilling, setListBilling] = useState<DriverMe['billing']>();
  const [tab, setTab] = useState<Tab>('to_sign');
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signStatus, setSignStatus] = useState('');
  const [signLaunchHref, setSignLaunchHref] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      const data = await driverApi.getDocuments('ALL');
      setDocuments(data.documents || []);
      setListBilling(data.billing);
    } catch (err) {
      console.error(err);
      if (!opts?.silent) {
        alert(err instanceof Error ? err.message : 'Ошибка загрузки');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const signParam = new URLSearchParams(window.location.search).get('sign');
    if (signParam) {
      pollRef.current = setInterval(async () => {
        try {
          const session = await driverApi.getSignSession(signParam);
          if (session.status === 'COMPLETED' || session.status === 'PARTIAL') {
            if (pollRef.current) clearInterval(pollRef.current);
            const url = new URL(window.location.href);
            url.searchParams.delete('sign');
            window.history.replaceState({}, '', url.pathname);
            await load({ silent: true });
          }
        } catch {
          /* ignore */
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const toSign = useMemo(() => documents.filter(isSignable), [documents]);
  const signed = useMemo(() => documents.filter((d) => !isSignable(d)), [documents]);
  const visible = tab === 'to_sign' ? toSign : signed;

  const paywall =
    listBilling ||
    billing ||
    toSign.find((doc) => doc.source === 'yandex' && doc.billing?.blocked)?.billing;
  const signBlocked = Boolean(paywall?.blocked && paywall?.payer === 'driver');

  const handleSignAll = async () => {
    if (toSign.length === 0) return;
    if (signBlocked) {
      alert(paywall?.message || t.payBeforeSign);
      onPay?.();
      return;
    }
    setSigning(true);
    setSignStatus(t.preparing);
    setSignLaunchHref(null);
    try {
      const session = await driverApi.createSignSession(toSign.map((d) => d.id));
      const href = resolveEgovLaunchHref(session.eGovMobileLaunchLink);
      setSignLaunchHref(href);
      setSignStatus(t.signHint);
      const poll = setInterval(async () => {
        try {
          const st = await driverApi.getSignSession(session.sessionId);
          if (st.status === 'COMPLETED' || st.status === 'PARTIAL' || st.status === 'EXPIRED') {
            clearInterval(poll);
            setSigning(false);
            setSignStatus('');
            setSignLaunchHref(null);
            if (st.status === 'EXPIRED') alert('Время подписания истекло');
            await load({ silent: true });
          }
        } catch {
          /* ignore */
        }
      }, 3000);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Ошибка подписания');
      setSigning(false);
      setSignStatus('');
      setSignLaunchHref(null);
    }
  };

  return (
    <div className="app-container driver-cabinet">
      <DriverHeader t={t} userName={userName} onMenu={onMenu} />
      <main className="main-content driver-main">
        <div className="section-header driver-section-header">
          <h2>{t.documents}</h2>
          <button
            type="button"
            className="driver-icon-btn"
            onClick={() => load()}
            disabled={loading}
            aria-label={t.refresh}
            title={t.refresh}
          >
            <svg
              className={loading ? 'spin' : undefined}
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-2.6-6.4" />
              <path d="M21 3v6h-6" />
            </svg>
          </button>
        </div>

        <div className="driver-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'to_sign'}
            className={tab === 'to_sign' ? 'active' : undefined}
            onClick={() => setTab('to_sign')}
          >
            {t.toSign}{toSign.length ? ` (${toSign.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'signed'}
            className={tab === 'signed' ? 'active' : undefined}
            onClick={() => setTab('signed')}
          >
            {t.signed}{signed.length ? ` (${signed.length})` : ''}
          </button>
        </div>

        {signBlocked && (
          <div className="driver-sign-status">
            <p>{paywall?.message || t.payBeforeSign}</p>
            {onPay && (
              <button type="button" className="btn-apply" onClick={onPay}>
                {t.goPay}
              </button>
            )}
          </div>
        )}

        {signStatus && (
          <div className="driver-sign-status">
            <p>{signStatus}</p>
            {signLaunchHref && (
              <a href={signLaunchHref} className="btn-apply">
                {t.openEgov}
              </a>
            )}
          </div>
        )}

        <div className="driver-doc-list">
          {loading && documents.length === 0 ? (
            <div className="no-documents">{t.loading}</div>
          ) : visible.length === 0 ? (
            <div className="no-documents">{tab === 'to_sign' ? t.emptyToSign : t.emptySigned}</div>
          ) : (
            visible.map((doc) => (
              <button
                key={doc.id}
                type="button"
                className="driver-doc-card"
                onClick={() => onOpenDoc(doc.id)}
              >
                <div className="driver-doc-body">
                  <div className="driver-doc-number">{docTitle(doc)}</div>
                  <div className="driver-doc-status" style={{ background: statusColor(doc) }}>
                    {doc.statusLabel || doc.status}
                  </div>
                  <div className="driver-doc-party">
                    {t.buyer}: {doc.buyerName || '—'}
                  </div>
                  <div className="driver-doc-party">
                    {t.seller}: {doc.sellerName || doc.fio || '—'}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {tab === 'to_sign' && (
          <div className="driver-sign-bar">
            <button
              type="button"
              className="btn-apply"
              disabled={signing || toSign.length === 0 || signBlocked}
              onClick={() => void handleSignAll()}
            >
              {signing ? t.signing : t.signAll}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};
